import React, { useEffect, useRef, useState } from 'react'
import { useStrategies } from './hooks'
import { FilterValue } from 'antd/es/table/interface'
import {
  Badge,
  Button,
  DatePicker,
  Input,
  InputRef,
  Space,
  Table,
  TimeRangePickerProps,
} from 'antd'
import { CButton, CCardBody } from '@coreui/react-pro'
import { ColumnsType } from 'antd/lib/table'
import { path } from '../../../../libs'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { AccountStrategy, DataIndex, Strategy } from './types'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

export const StrategiesList = () => {
  const {
    strategies,
    totalCountOfStrategies,
    loading,
    onChangeStrategiesList,
  } = useStrategies()

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('publishedAt')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeStrategiesList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  // TODO - доработать фильтрацию дат после доработки бэка

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  const getColumnSearchDateProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      close,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'flex' }}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
          open={isCalendarOpen}
          onClick={() => setIsCalendarOpen(true)}
          allowEmpty={[true, true]}
          onCalendarChange={() => setIsCalendarOpen(false)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1]))
        : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<Strategy> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
    },
    {
      title: 'Start',
      dataIndex: 'publishedAt',
      ...getColumnSearchDateProps('publishedAt'),
      defaultSortOrder: 'descend',
      sorter: true,
      render: (item, record) => record.startedAt,
    },
    {
      title: 'Deleted',
      dataIndex: 'deletedAt',
      defaultSortOrder: 'descend',
      filterMultiple: false,
      filters: [
        {
          text: 'All',
          value: '',
        },
        {
          text: 'Yes',
          value: 'not null',
        },
        {
          text: 'No',
          value: 'null',
        },
      ],
      defaultFilteredValue: ['null'],
    },
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
      ...getColumnSearchProps('title'),
      render: (title: string) => title,
    },
    {
      title: 'MT Login',
      dataIndex: 'account',
      ...getColumnSearchProps('account.mtLogin'),
      render: (account: AccountStrategy) =>
        account?.accountPlatform + ' ' + account?.mtLogin,
    },
    {
      title: 'Client',
      ...getColumnSearchProps('client.fullName'),
      render: (strategyItem: Strategy) => (
        <div>
          {strategyItem.client?.isTest ? (
            <Badge count={'Test'} offset={[-5, -10]}>
              <a
                href={path.clients.details(String(strategyItem.clientId))}
                target="_blank"
              >
                {strategyItem.client.fullName}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(strategyItem.clientId))}
              target="_blank"
            >
              {strategyItem.client.fullName}
            </a>
          )}
        </div>
      ),
      sorter: (a, b) => a.client.fullName.localeCompare(b.client.fullName),
    },
    {
      title: 'Balance',
      dataIndex: 'account',
      render: (account: AccountStrategy) => account?.balance,
      sorter: true,
    },

    {
      title: 'Commission',
      dataIndex: 'commission',
      key: 'commission',
      ...getColumnSearchProps('commission'),
      render: (commission: number) => commission,
      sorter: true,
    },
    {
      title: 'Commission %',
      dataIndex: 'percentCommission',
      key: 'percentCommission',
      ...getColumnSearchProps('percentCommission'),
      render: (percentCommission: number) =>
        (percentCommission * 100).toFixed(2),
      sorter: true,
    },
    {
      title: 'Investors',
      dataIndex: 'investors',
      ...getColumnSearchProps('investors'),
      render: (investors: number, record) =>
        investors + record.numAdditionalInvestors,
      sorter: true,
    },
    {
      title: 'AI Assessment',
      dataIndex: 'aiAssessment',
      key: 'aiAssessment',
      render: (aiAssessment: number) => aiAssessment,
      sorter: true,
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.copytrade.strategies.details(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={strategies}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="strategiesId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfStrategies,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
